'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

import Snackbar from '@components/ui/Snackbar';

interface UseSnackbarCallbackProp {
  message: string;
  renderIcon?: boolean;
  error?: boolean;
}

interface SnackbarState {
  show: boolean;
  message: string;
  renderIcon?: boolean;
  error?: boolean;
}

const SnackbarContext = createContext<
  ((props: UseSnackbarCallbackProp) => void) | undefined
>(undefined);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    show: false,
    message: '',
    renderIcon: false,
    error: false,
  });

  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const showSnackbar = useCallback(
    (props: UseSnackbarCallbackProp) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setSnackbar({ show: true, ...props });

      const newTimeoutId = setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, show: false, message: '' }));
      }, 3000);

      setTimeoutId(newTimeoutId);
    },
    [timeoutId]
  );

  return (
    <SnackbarContext.Provider value={showSnackbar}>
      {children}
      {snackbar.show && (
        <Snackbar
          key={timeoutId?.toString()}
          text={snackbar.message}
          {...snackbar}
        />
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): ((props: UseSnackbarCallbackProp) => void) => {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
